import {
  RecursivePartialSiteAllowStringValues,
  SiteAllowStringValues,
} from "@inrange/building-manager-api-client/models-site";
import {
  SiteCalculationsContext,
  useContextTS,
} from "@inrange/shared-components";
import {
  currencySymbol,
  formatBatteryModel,
} from "@inrange/theme-components/formatting";
import React from "react";
import { Col, Form, Row } from "react-bootstrap";
import CollapsibleCard from "../CollapsibleCard";
import { currencyValue, formatNumber, parseAsNumber } from "./utils";

const BatterySummary = ({
  site,
  setSite,
}: {
  site: SiteAllowStringValues;
  setSite: (site: RecursivePartialSiteAllowStringValues) => void;
}) => {
  const { errors, siteCalculations } = useContextTS(SiteCalculationsContext);

  const peakShiftingValue = (
    <Col sm={3}>
      <Form.Group className="mb-3" controlId="arbitrageY1">
        <Form.Label>Peak shifting value (Y1)</Form.Label>
        <Form.Control
          disabled
          value={currencyValue(
            siteCalculations.financialModelsInrange?.tariffArbitrageRevenue
              .grossProcurementValue || 0,
            siteCalculations.currencyCode
          )}
        />
      </Form.Group>
    </Col>
  );

  return (
    <CollapsibleCard
      title={"Battery"}
      summary={
        siteCalculations.totalBatterySummary
          ? `${siteCalculations.totalBatterySummary.batteryInstalls.map((b) => formatBatteryModel(b)).join(", ")}`
          : undefined
      }
      className="mt-2"
      dataTestId="battery-section"
      id="battery-section"
      initialOpen={false}
    >
      <>
        {siteCalculations.totalBatterySummary &&
          siteCalculations.totalBatterySummary.batteryInstalls.map(
            (battery, index) => (
              <React.Fragment key={index}>
                <Row className="align-items-end">
                  <Col sm={3} className="mb-3">
                    <div>
                      <strong>{battery.modelName}</strong>
                    </div>
                  </Col>
                  <Col sm={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Num batteries</Form.Label>
                      <Form.Control
                        type="text"
                        value={battery.count}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group
                      className="mb-3"
                      controlId={`batteryFunderCostPerKwp${index}`}
                    >
                      <Form.Label>
                        Cost/kWp to funder ({currencySymbol(site.currencyCode)}
                        /kWp)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={
                          parseAsNumber(site.totalInstalledCapacity) > 0
                            ? currencyValue(
                                (parseAsNumber(
                                  site.costInputsBattery.costs[
                                    battery.modelName!
                                  ].hardwareCost
                                ) +
                                  parseAsNumber(
                                    site.costInputsBattery.costs[
                                      battery.modelName!
                                    ].installationCost
                                  )) /
                                  parseAsNumber(site.totalInstalledCapacity),
                                site.currencyCode
                              )
                            : ""
                        }
                        disabled={true}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group
                      className="mb-3"
                      controlId={`batteryMfrCostKwh${index}`}
                    >
                      <Form.Label>
                        Cost/kWh to mfr ({currencySymbol(site.currencyCode)}
                        /kWh)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={currencyValue(
                          (parseAsNumber(
                            site.costInputsInrange!.batteryCosts[
                              battery.modelName
                            ]?.hardwareCost ||
                              site.costInputsBattery.costs[battery.modelName]
                                ?.hardwareCost
                          ) +
                            parseAsNumber(
                              site.costInputsInrange!.batteryCosts[
                                battery.modelName
                              ]?.installationCost ||
                                site.costInputsBattery.costs[battery.modelName]
                                  ?.installationCost
                            )) /
                            ((parseAsNumber(battery.totalCapacityMwh) /
                              parseAsNumber(battery.count)) *
                              1000),
                          site.currencyCode
                        )}
                        disabled={true}
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        Per batt. HW to funder (
                        {currencySymbol(site.currencyCode)})
                      </Form.Label>
                      <Form.Control
                        type="text"
                        value={formatNumber(
                          site.costInputsBattery?.costs?.[battery.modelName]
                            ?.hardwareCost || "",
                          2,
                          6,
                          false
                        )}
                        onChange={(e) => {
                          setSite({
                            costInputsBattery: {
                              costs: {
                                [battery.modelName!]: {
                                  hardwareCost: formatNumber(
                                    e.target.value,
                                    2,
                                    6,
                                    false
                                  ),
                                },
                              },
                            },
                          });
                        }}
                        isInvalid={
                          !!errors[
                            `costInputsBattery.costs.${battery.modelName}.hardwareCost`
                          ]
                        }
                        data-testid={"batteryCost"}
                      />
                      <Form.Control.Feedback type="invalid">
                        {
                          errors[
                            `costInputsBattery.costs.${battery.modelName}.hardwareCost`
                          ]
                        }
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group
                      className="mb-3"
                      controlId="batteryInstallationCost"
                    >
                      <Form.Label>Per batt. install to funder</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatNumber(
                          site.costInputsBattery?.costs?.[battery.modelName]
                            ?.installationCost || "",
                          2,
                          6,
                          false
                        )}
                        onChange={(e) => {
                          setSite({
                            costInputsBattery: {
                              costs: {
                                [battery.modelName!]: {
                                  installationCost: formatNumber(
                                    e.target.value,
                                    2,
                                    6,
                                    false
                                  ),
                                },
                              },
                            },
                          });
                        }}
                        isInvalid={
                          !!errors[
                            `costInputsBattery.costs.${battery.modelName}.installationCost`
                          ]
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {
                          errors[
                            `costInputsBattery.costs.${battery.modelName}.installationCost`
                          ]
                        }
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total HW cost to funder</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatNumber(
                          parseAsNumber(
                            site.costInputsBattery?.costs?.[battery.modelName]
                              ?.hardwareCost || ""
                          ) * parseAsNumber(battery.count),
                          2,
                          6,
                          false
                        )}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total install cost to funder</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatNumber(
                          parseAsNumber(
                            site.costInputsBattery?.costs?.[battery.modelName]
                              ?.installationCost || ""
                          ) * parseAsNumber(battery.count),
                          2,
                          6,
                          false
                        )}
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group
                      className="mb-3"
                      controlId="irBatteryHardwareCost"
                    >
                      <Form.Label>Per batt. mfr. HW quote</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatNumber(
                          site.costInputsInrange?.batteryCosts?.[
                            battery.modelName
                          ]?.hardwareCost || "",
                          2,
                          6,
                          false
                        )}
                        onChange={(e) => {
                          setSite({
                            costInputsInrange: {
                              batteryCosts: {
                                [battery.modelName!]: {
                                  hardwareCost: formatNumber(
                                    e.target.value,
                                    2,
                                    6,
                                    false
                                  ),
                                },
                              },
                            },
                          });
                        }}
                        isInvalid={
                          !!errors[
                            `costInputsInrange.batteryCosts.${battery.modelName}.hardwareCost`
                          ]
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {
                          errors[
                            `costInputsInrange.batteryCosts.${battery.modelName}.hardwareCost`
                          ]
                        }
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group
                      className="mb-3"
                      controlId="irBatteryInstallationCost"
                    >
                      <Form.Label>Per batt. installer quote</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatNumber(
                          site.costInputsInrange?.batteryCosts?.[
                            battery.modelName
                          ]?.installationCost || "",
                          2,
                          6,
                          false
                        )}
                        onChange={(e) => {
                          setSite({
                            costInputsInrange: {
                              batteryCosts: {
                                [battery.modelName!]: {
                                  installationCost: formatNumber(
                                    e.target.value,
                                    2,
                                    6,
                                    false
                                  ),
                                },
                              },
                            },
                          });
                        }}
                        isInvalid={
                          !!errors[
                            `costInputsInrange.costs.${battery.modelName}.installationCost`
                          ]
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {
                          errors[
                            `costInputsInrange.costs.${battery.modelName}.installationCost`
                          ]
                        }
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total Mfr. HW quote</Form.Label>
                      <Form.Control
                        type="text"
                        value={
                          site.costInputsInrange?.batteryCosts?.[
                            battery.modelName
                          ]?.hardwareCost
                            ? formatNumber(
                                parseAsNumber(
                                  site.costInputsInrange?.batteryCosts?.[
                                    battery.modelName
                                  ]?.hardwareCost || ""
                                ) * parseAsNumber(battery.count),
                                2,
                                6,
                                false
                              )
                            : ""
                        }
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  <Col sm={3}>
                    <Form.Group className="mb-3">
                      <Form.Label>Total batt. installer quote</Form.Label>
                      <Form.Control
                        type="text"
                        value={
                          site.costInputsInrange?.batteryCosts?.[
                            battery.modelName
                          ]?.installationCost
                            ? formatNumber(
                                parseAsNumber(
                                  site.costInputsInrange?.batteryCosts?.[
                                    battery.modelName
                                  ]?.installationCost || ""
                                ) * parseAsNumber(battery.count),
                                2,
                                6,
                                false
                              )
                            : ""
                        }
                        disabled
                      />
                    </Form.Group>
                  </Col>
                  {siteCalculations.totalBatterySummary!.batteryInstalls
                    .length === 1 && <>{peakShiftingValue}</>}
                </Row>
                {siteCalculations.totalBatterySummary!.batteryInstalls.length >
                  1 && <hr />}
              </React.Fragment>
            )
          )}
        {siteCalculations.totalBatterySummary!.batteryInstalls.length > 1 && (
          <Row>{peakShiftingValue}</Row>
        )}
      </>
    </CollapsibleCard>
  );
};

export default BatterySummary;
